import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2, ArrowLeft, MailCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const strong = (p) => p.length >= 10 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /\d/.test(p);

function Shell({ title, subtitle, children }) {
  return (
    <section className="py-20 min-h-[70vh] flex items-center">
      <div className="max-w-md mx-auto px-4 w-full rounded-2xl border border-slate-800 bg-slate-900/60 p-8">
        <img src="/logo.png" alt="Integrity Delivered" className="h-16 w-auto mb-6 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" />
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-amber-500 flex items-center justify-center"><KeyRound className="w-5 h-5 text-slate-950" /></div>
          <div>
            <h1 className="text-2xl font-extrabold uppercase display text-white">{title}</h1>
            <div className="text-xs text-slate-400">{subtitle}</div>
          </div>
        </div>
        <div className="mt-8">{children}</div>
        <Link to="/admin/login" data-testid="reset-back-to-login" className="mt-6 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-amber-400"><ArrowLeft className="w-3.5 h-3.5" /> Back to sign in</Link>
      </div>
    </section>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <Shell title="Forgot password" subtitle="We'll email you a one-time reset link">
      {sent ? (
        <div data-testid="forgot-password-sent" className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-sm text-emerald-100 flex gap-3">
          <MailCheck className="w-5 h-5 text-emerald-400 shrink-0" />
          <div>If <strong>{email}</strong> belongs to an admin account, a reset link is on its way. It expires in 1 hour — check your spam folder if you don't see it.</div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Admin email</Label>
            <Input data-testid="forgot-password-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
          </div>
          <Button data-testid="forgot-password-submit" type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider">
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Send reset link
          </Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const valid = strong(pw) && pw === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated — sign in with your new password");
      nav("/admin/login");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  if (!token) {
    return <Shell title="Reset password" subtitle="Invalid link"><p data-testid="reset-password-missing-token" className="text-sm text-rose-300">This reset link is missing its token. Please request a new one from the <Link to="/admin/forgot-password" className="underline">forgot password</Link> page.</p></Shell>;
  }

  return (
    <Shell title="Choose a new password" subtitle="10+ characters with upper, lower, and a number">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>New password</Label>
          <Input data-testid="reset-password-new" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" autoComplete="new-password" />
        </div>
        <div>
          <Label>Confirm new password</Label>
          <Input data-testid="reset-password-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" autoComplete="new-password" />
          {confirm && pw !== confirm && <div className="text-xs text-rose-400 mt-1">Passwords don't match</div>}
          {pw && !strong(pw) && <div className="text-xs text-slate-500 mt-1">Needs 10+ characters, an uppercase letter, a lowercase letter, and a number</div>}
        </div>
        <Button data-testid="reset-password-submit" type="submit" disabled={!valid || loading} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Update password
        </Button>
      </form>
    </Shell>
  );
}
